import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../supabase";
import { COUNTRY_CODES } from "../../../utils/countryCode";
import { subscribeToResendAudience } from "../../../hooks/useResendAudience";
import useSnackbarNotification from "../../../hooks/useSnackbarNotification";

const isDeleteConfirmed = (text) => {
    const normalized = text.toLowerCase().replace(/\s+/g, " ").trim();
    return normalized.includes("delete") && normalized.includes("account");
};

const AccountInformation = () => {
    const { user } = useAuth();
    const location = useLocation();
    const { showSuccess, showError } = useSnackbarNotification();

    const isEmployerRoute = location.pathname.includes("/employer/");

    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        countryCode: "+27",
        cell: "",
        date_of_birth: "",
        sex: "",
        training_level: "",
        employment_status: "",
        field_of_interest: "",
        country: "",
        medical_school: "",
        joining_medhi_advisory_fellowship: "",
        medhi_advisory_fellowship_specialty: "",
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const [accountMessage, setAccountMessage] = useState(null);
    const [passwordMessage, setPasswordMessage] = useState(null);
    const [deleteMessage, setDeleteMessage] = useState(null);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState("");

    const [medicalSchools, setMedicalSchools] = useState([]);
    const [medicalSchoolsLoading, setMedicalSchoolsLoading] = useState(false);

    const parseCellNumber = useCallback((full) => {
        if (!full || typeof full !== "string") {
            return { countryCode: "+27", cell: "" };
        }
        const matchingCountry = COUNTRY_CODES.find((c) =>
            full.startsWith(c.code)
        );
        if (matchingCountry) {
            return {
                countryCode: matchingCountry.code,
                cell: full.substring(matchingCountry.code.length),
            };
        }
        return { countryCode: "+27", cell: full };
    }, []);

    useEffect(() => {
        const loadSchools = async () => {
            try {
                setMedicalSchoolsLoading(true);
                const { data, error } = await supabase
                    .from("medical_schools")
                    .select("id, school, country")
                    .order("school", { ascending: true });
                if (error) {
                    setMedicalSchools([]);
                    return;
                }
                setMedicalSchools(data || []);
            } catch {
                setMedicalSchools([]);
            } finally {
                setMedicalSchoolsLoading(false);
            }
        };
        loadSchools();
    }, []);

    useEffect(() => {
        if (!user?.user_metadata && !user?.email) return;

        const md = user.user_metadata || {};
        const employerCompanyInfo = md.employer_profile?.companyInfo;
        const { countryCode, cell } = parseCellNumber(md.cell_number);

        if (isEmployerRoute && employerCompanyInfo) {
            setFormData({
                first_name:
                    employerCompanyInfo.firstName || md.first_name || "",
                last_name: employerCompanyInfo.lastName || md.last_name || "",
                email:
                    employerCompanyInfo.workEmail ||
                    md.email ||
                    user.email ||
                    "",
                countryCode,
                cell,
                date_of_birth: md.date_of_birth || "",
                sex: md.sex || "",
                training_level: md.training_level || "",
                employment_status: md.employment_status || "",
                field_of_interest: md.field_of_interest || "",
                country: md.country || "",
                medical_school: md.medical_school || "",
                joining_medhi_advisory_fellowship:
                    md.joining_medhi_advisory_fellowship || "",
                medhi_advisory_fellowship_specialty:
                    md.medhi_advisory_fellowship_specialty || "",
            });
        } else {
            setFormData({
                first_name: md.first_name || "",
                last_name: md.last_name || "",
                email: md.email || user.email || "",
                countryCode,
                cell,
                date_of_birth: md.date_of_birth || "",
                sex: md.sex || "",
                training_level: md.training_level || "",
                employment_status: md.employment_status || "",
                field_of_interest: md.field_of_interest || "",
                country: md.country || "",
                medical_school: md.medical_school || "",
                joining_medhi_advisory_fellowship:
                    md.joining_medhi_advisory_fellowship || "",
                medhi_advisory_fellowship_specialty:
                    md.medhi_advisory_fellowship_specialty || "",
            });
        }
    }, [user, isEmployerRoute, parseCellNumber]);

    useEffect(() => {
        if (!accountMessage) return;
        const t = setTimeout(() => setAccountMessage(null), 5000);
        return () => clearTimeout(t);
    }, [accountMessage]);

    useEffect(() => {
        if (!passwordMessage) return;
        const t = setTimeout(() => setPasswordMessage(null), 5000);
        return () => clearTimeout(t);
    }, [passwordMessage]);

    useEffect(() => {
        if (!deleteMessage || deleteMessage.type !== "error") return;
        const t = setTimeout(() => setDeleteMessage(null), 8000);
        return () => clearTimeout(t);
    }, [deleteMessage]);

    const medicalSchoolOptions = medicalSchools.map((school) => ({
        value: school.school,
        label: `${school.school} (${school.country})`,
    }));

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({ ...prev, [name]: value }));
    };

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const handleSaveAccount = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setAccountMessage(null);

        try {
            if (
                formData.joining_medhi_advisory_fellowship === "true" &&
                !formData.medhi_advisory_fellowship_specialty
            ) {
                const text = "Please select a Medhi Advisory Fellowship specialty.";
                setAccountMessage({ type: "error", text });
                showError(text);
                setIsLoading(false);
                return;
            }

            const fullPhoneNumber = formData.cell
                ? `${formData.countryCode}${formData.cell}`
                : "";

            const updateData = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                cell_number: fullPhoneNumber,
                date_of_birth: formData.date_of_birth,
                sex: formData.sex,
                country: formData.country,
                training_level: formData.training_level,
                employment_status: formData.employment_status,
                field_of_interest: formData.field_of_interest,
                medical_school: formData.medical_school,
                joining_medhi_advisory_fellowship:
                    formData.joining_medhi_advisory_fellowship,
                medhi_advisory_fellowship_specialty:
                    formData.medhi_advisory_fellowship_specialty,
            };

            if (isEmployerRoute) {
                const existingEmployerProfile = user?.user_metadata?.employer_profile || {};
                const existingCompanyInfo = existingEmployerProfile.companyInfo || {};
                updateData.employer_profile = {
                    ...existingEmployerProfile,
                    companyInfo: {
                        ...existingCompanyInfo,
                        firstName: formData.first_name,
                        lastName: formData.last_name,
                        workEmail:
                            existingCompanyInfo.workEmail || formData.email,
                    },
                };
            }

            const { error } = await supabase.auth.updateUser({
                data: updateData,
            });

            if (error) throw error;

            if (formData.joining_medhi_advisory_fellowship === "true") {
                try {
                    await subscribeToResendAudience({
                        email: user?.email || "",
                        firstName: formData.first_name,
                        lastName: formData.last_name,
                        role: user?.user_metadata?.role || "Employee",
                    });
                } catch {
                    /* non-fatal */
                }
            }

            setAccountMessage({
                type: "success",
                text: "Account information updated successfully.",
            });
            showSuccess("Account information updated successfully");
        } catch (err) {
            const text =
                err.message || "Failed to update account information.";
            setAccountMessage({ type: "error", text });
            showError(text);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setIsPasswordLoading(true);
        setPasswordMessage(null);

        if (
            !passwordData.currentPassword ||
            !passwordData.newPassword ||
            !passwordData.confirmPassword
        ) {
            setPasswordMessage({
                type: "error",
                text: "Please fill in all password fields.",
            });
            setIsPasswordLoading(false);
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setPasswordMessage({
                type: "error",
                text: "New password must be at least 6 characters.",
            });
            setIsPasswordLoading(false);
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPasswordMessage({
                type: "error",
                text: "New passwords do not match.",
            });
            setIsPasswordLoading(false);
            return;
        }

        try {
            const { error: verifyError } =
                await supabase.auth.signInWithPassword({
                    email: user?.email || "",
                    password: passwordData.currentPassword,
                });

            if (verifyError) {
                setPasswordMessage({
                    type: "error",
                    text: "Current password is incorrect.",
                });
                setIsPasswordLoading(false);
                return;
            }

            const { error } = await supabase.auth.updateUser({
                password: passwordData.newPassword,
            });

            if (error) throw error;

            setPasswordMessage({
                type: "success",
                text: "Password updated successfully.",
            });
            showSuccess("Password updated successfully");
            setPasswordData({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
            });
        } catch (err) {
            const text = err.message || "Failed to update password.";
            setPasswordMessage({ type: "error", text });
            showError(text);
        } finally {
            setIsPasswordLoading(false);
        }
    };

    const initiateDeleteAccount = () => {
        setShowDeleteConfirmation(true);
        setDeleteMessage(null);
        setDeleteConfirmationText("");
    };

    const cancelDeleteAccount = () => {
        setShowDeleteConfirmation(false);
        setDeleteConfirmationText("");
        setDeleteMessage(null);
    };

    const handleDeleteAccount = async () => {
        if (!isDeleteConfirmed(deleteConfirmationText)) {
            setDeleteMessage({
                type: "error",
                text: 'Please confirm by including the words "delete" and "account".',
            });
            return;
        }

        setIsDeleteLoading(true);
        setDeleteMessage(null);

        try {
            const { error: fnError } = await supabase.functions.invoke(
                "delete-user",
                { body: { user_id: user?.id } }
            );

            if (fnError) throw fnError;

            setDeleteMessage({
                type: "success",
                text: "Account deleted successfully. Redirecting…",
            });
            showSuccess("Account deleted");

            try {
                await supabase.auth.signOut();
            } catch {
                /* ignore */
            }
            try {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith("sb-")) keysToRemove.push(key);
                }
                keysToRemove.forEach((k) => localStorage.removeItem(k));
            } catch {
                /* ignore */
            }

            setTimeout(() => {
                window.location.href = "/";
            }, 1500);
        } catch (err) {
            console.error("Delete account error:", err);
            setDeleteMessage({
                type: "error",
                text:
                    err.message ||
                    "Failed to delete account. Please try again.",
            });
            showError(err.message || "Failed to delete account");
        } finally {
            setIsDeleteLoading(false);
        }
    };

    const inputClass =
        "w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent";
    const selectClass =
        "w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none appearance-none bg-white pr-10";

    return (
        <div className="flex-1 min-w-0">
            <h2 className="font-bold text-xl mb-6 text-gray-900">
                Account information
            </h2>

            {accountMessage && (
                <div
                    className={`mb-4 p-3 rounded-lg text-sm ${
                        accountMessage.type === "success"
                            ? "bg-green-50 text-green-800 border border-green-100"
                            : "bg-red-50 text-red-800 border border-red-100"
                    }`}
                >
                    {accountMessage.text}
                </div>
            )}

            <form
                onSubmit={handleSaveAccount}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-10"
            >
                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        First name *
                    </label>
                    <input
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className={inputClass}
                        required
                    />
                </div>
                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Surname *
                    </label>
                    <input
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={inputClass}
                        required
                    />
                </div>
                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Email address
                    </label>
                    <input
                        name="email"
                        type="email"
                        value={formData.email}
                        className={`${inputClass} bg-gray-100 cursor-not-allowed`}
                        disabled
                        title="Email cannot be changed"
                    />
                </div>
                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Cell number (optional)
                    </label>
                    <div className="flex">
                        <select
                            name="countryCode"
                            value={formData.countryCode}
                            onChange={handleChange}
                            className="appearance-none w-28 flex-shrink-0 px-2 py-2 border border-gray-300 rounded-l-full border-r-0 bg-gray-50 text-sm focus:outline-none"
                        >
                            {COUNTRY_CODES.map((country) => (
                                <option
                                    key={`${country.code}-${country.country}`}
                                    value={country.code}
                                >
                                    {country.flag} {country.code}
                                </option>
                            ))}
                        </select>
                        <input
                            name="cell"
                            value={formData.cell}
                            onChange={handleChange}
                            type="tel"
                            placeholder="1234567890"
                            className={`${inputClass} rounded-l-none border-l-0 flex-1`}
                        />
                    </div>
                </div>

                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Date of birth *
                    </label>
                    <input
                        name="date_of_birth"
                        type="date"
                        value={formatDateForInput(formData.date_of_birth)}
                        onChange={handleChange}
                        className={inputClass}
                        required
                    />
                </div>
                <div className="relative">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Sex *
                    </label>
                    <select
                        name="sex"
                        value={formData.sex}
                        onChange={handleChange}
                        className={selectClass}
                        required
                    >
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="intersex">Intersex</option>
                        <option value="other">Other</option>
                        <option value="prefer-not-to-say">
                            Prefer not to say
                        </option>
                    </select>
                </div>

                <div className="relative md:col-span-2">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Level of training / expertise *
                    </label>
                    <select
                        name="training_level"
                        value={formData.training_level}
                        onChange={handleChange}
                        className={selectClass}
                        required
                    >
                        <option value="">Select</option>
                        <option value="1st-year">1st year</option>
                        <option value="2nd-year">2nd year</option>
                        <option value="3rd-year">3rd year</option>
                        <option value="4th-year">4th year</option>
                        <option value="5th-year">5th year</option>
                        <option value="6th-year">6th year</option>
                        <option value="medical-intern">Medical intern</option>
                        <option value="mo-gps">MO / GPs</option>
                        <option value="registrar-resident">
                            Registrar / Resident
                        </option>
                        <option value="student">Student</option>
                        <option value="junior">Junior</option>
                        <option value="senior">Senior</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <div className="relative">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Employment status *
                    </label>
                    <select
                        name="employment_status"
                        value={formData.employment_status}
                        onChange={handleChange}
                        className={selectClass}
                        required
                    >
                        <option value="">Select</option>
                        <option value="full-time">Full-time</option>
                        <option value="part-time">Part-time</option>
                        <option value="in-training">In training</option>
                        <option value="unemployed">Unemployed</option>
                        <option value="retired">Retired</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Medical school
                    </label>
                    <Select
                        value={
                            medicalSchoolOptions.find(
                                (o) => o.value === formData.medical_school
                            ) || null
                        }
                        onChange={(opt) =>
                            setFormData((p) => ({
                                ...p,
                                medical_school: opt?.value || "",
                            }))
                        }
                        options={medicalSchoolOptions}
                        isLoading={medicalSchoolsLoading}
                        classNamePrefix="account-med-school"
                        placeholder={
                            medicalSchools.length
                                ? "Select medical school…"
                                : "List unavailable — type saved in profile if set"
                        }
                        isClearable
                        isSearchable
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderRadius: 9999,
                                borderColor: "#d1d5db",
                                minHeight: 42,
                                paddingLeft: 4,
                            }),
                            menu: (base) => ({
                                ...base,
                                borderRadius: 12,
                            }),
                        }}
                    />
                </div>

                <div>
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Country
                    </label>
                    <input
                        type="text"
                        value={countryList().getLabel(formData.country) || ""}
                        readOnly
                        className={`${inputClass} bg-gray-100 cursor-not-allowed`}
                        disabled
                    />
                </div>

                <div className="md:col-span-2 relative">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Speciality of interest *
                    </label>
                    <select
                        name="field_of_interest"
                        value={formData.field_of_interest}
                        onChange={handleChange}
                        className={selectClass}
                        required
                    >
                        <option value="">Select</option>
                        <option value="internal-medicine">Internal medicine</option>
                        <option value="general-surgery">General surgery</option>
                        <option value="emergency-medicine">Emergency medicine</option>
                        <option value="trauma">Trauma</option>
                        <option value="orthopaedics">Orthopaedics</option>
                        <option value="paediatrics">Paediatrics</option>
                        <option value="gynaecology">Gynaecology</option>
                        <option value="obstetrics">Obstetrics</option>
                        <option value="psychiatry">Psychiatry</option>
                        <option value="clinical-medicine">Clinical medicine</option>
                        <option value="business-consulting">
                            Business / consulting
                        </option>
                        <option value="healthtech">HealthTech</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <div className="md:col-span-2">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Are you joining Medhi Advisory Fellowship?
                    </label>
                    <Select
                        value={
                            [
                                { value: "true", label: "Yes" },
                                { value: "false", label: "No" },
                            ].find(
                                (o) =>
                                    o.value ===
                                    formData.joining_medhi_advisory_fellowship
                            ) || null
                        }
                        onChange={(opt) =>
                            setFormData((p) => ({
                                ...p,
                                joining_medhi_advisory_fellowship:
                                    opt?.value || "",
                                medhi_advisory_fellowship_specialty:
                                    opt?.value === "false"
                                        ? ""
                                        : p.medhi_advisory_fellowship_specialty,
                            }))
                        }
                        options={[
                            { value: "true", label: "Yes" },
                            { value: "false", label: "No" },
                        ]}
                        classNamePrefix="account-maf"
                        placeholder="Select…"
                        isClearable
                        styles={{
                            control: (base) => ({
                                ...base,
                                borderRadius: 9999,
                                borderColor: "#d1d5db",
                                minHeight: 42,
                            }),
                            menu: (base) => ({ ...base, borderRadius: 12 }),
                        }}
                    />
                </div>

                {formData.joining_medhi_advisory_fellowship === "true" && (
                    <div className="md:col-span-2">
                        <label className="font-medium mb-1 block text-sm text-gray-800">
                            Medhi Advisory Fellowship specialty *
                        </label>
                        <Select
                            value={
                                [
                                    {
                                        value: "internal-medicine",
                                        label: "Internal medicine",
                                    },
                                    {
                                        value: "general-surgery",
                                        label: "General surgery",
                                    },
                                    {
                                        value: "orthopaedics",
                                        label: "Orthopaedics",
                                    },
                                    {
                                        value: "paediatrics",
                                        label: "Paediatrics",
                                    },
                                    {
                                        value: "obstetrics",
                                        label: "Obstetrics",
                                    },
                                ].find(
                                    (o) =>
                                        o.value ===
                                        formData.medhi_advisory_fellowship_specialty
                                ) || null
                            }
                            onChange={(opt) =>
                                setFormData((p) => ({
                                    ...p,
                                    medhi_advisory_fellowship_specialty:
                                        opt?.value || "",
                                }))
                            }
                            options={[
                                {
                                    value: "internal-medicine",
                                    label: "Internal medicine",
                                },
                                {
                                    value: "general-surgery",
                                    label: "General surgery",
                                },
                                { value: "orthopaedics", label: "Orthopaedics" },
                                { value: "paediatrics", label: "Paediatrics" },
                                { value: "obstetrics", label: "Obstetrics" },
                            ]}
                            classNamePrefix="account-maf-spec"
                            placeholder="Select…"
                            isClearable
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: 9999,
                                    borderColor: "#d1d5db",
                                    minHeight: 42,
                                }),
                                menu: (base) => ({ ...base, borderRadius: 12 }),
                            }}
                        />
                    </div>
                )}

                <div className="md:col-span-2">
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`px-8 py-2 rounded-full bg-black text-white font-medium text-sm ${
                            isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
                        }`}
                    >
                        {isLoading ? "Saving…" : "Save"}
                    </button>
                </div>
            </form>

            <h3 className="font-bold text-xl mb-4 text-gray-900">
                Change password
            </h3>
            {passwordMessage && (
                <div
                    className={`mb-4 p-3 rounded-lg text-sm ${
                        passwordMessage.type === "success"
                            ? "bg-green-50 text-green-800 border border-green-100"
                            : "bg-red-50 text-red-800 border border-red-100"
                    }`}
                >
                    {passwordMessage.text}
                </div>
            )}
            <form
                onSubmit={handleChangePassword}
                className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6"
            >
                <div className="relative">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Current password *
                    </label>
                    <input
                        name="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        className={`${inputClass} pr-10`}
                        autoComplete="current-password"
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-[30px] text-gray-500 text-xs"
                        onClick={() =>
                            setShowCurrentPassword((v) => !v)
                        }
                    >
                        {showCurrentPassword ? "Hide" : "Show"}
                    </button>
                </div>
                <div className="relative">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        New password *
                    </label>
                    <input
                        name="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        className={`${inputClass} pr-10`}
                        minLength={6}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-[30px] text-gray-500 text-xs"
                        onClick={() => setShowNewPassword((v) => !v)}
                    >
                        {showNewPassword ? "Hide" : "Show"}
                    </button>
                </div>
                <div className="relative md:col-span-2 max-w-md">
                    <label className="font-medium mb-1 block text-sm text-gray-800">
                        Confirm new password *
                    </label>
                    <input
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        className={`${inputClass} pr-10`}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-[30px] text-gray-500 text-xs"
                        onClick={() =>
                            setShowConfirmPassword((v) => !v)
                        }
                    >
                        {showConfirmPassword ? "Hide" : "Show"}
                    </button>
                </div>
                <div className="md:col-span-2">
                    <button
                        type="submit"
                        disabled={isPasswordLoading}
                        className={`px-8 py-2 rounded-full bg-black text-white font-medium text-sm ${
                            isPasswordLoading
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-gray-800"
                        }`}
                    >
                        {isPasswordLoading ? "Updating…" : "Save password"}
                    </button>
                </div>
            </form>

            <h3 className="font-bold text-xl mb-2 text-red-900">Delete account</h3>
            {deleteMessage && (
                <div
                    className={`mb-4 p-3 rounded-lg text-sm ${
                        deleteMessage.type === "success"
                            ? "bg-green-50 text-green-800 border border-green-100"
                            : "bg-red-50 text-red-800 border border-red-100"
                    }`}
                >
                    {deleteMessage.text}
                </div>
            )}

            {!showDeleteConfirmation ? (
                <>
                    <p className="text-gray-700 text-sm mb-4">
                        To permanently delete your account and associated data,
                        use the button below.{" "}
                        <strong className="text-red-700">
                            This cannot be undone.
                        </strong>
                    </p>
                    <button
                        type="button"
                        onClick={initiateDeleteAccount}
                        disabled={isDeleteLoading}
                        className="px-8 py-2 rounded-full border border-red-700 text-red-700 font-medium text-sm hover:bg-red-50 disabled:opacity-50"
                    >
                        Delete my account
                    </button>
                </>
            ) : (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-4">
                    <h4 className="text-lg font-semibold text-red-900 mb-3">
                        Confirm account deletion
                    </h4>
                    <p className="text-red-800 text-sm mb-4">
                        Type a phrase that includes both the words{" "}
                        <strong>delete</strong> and <strong>account</strong> to
                        confirm.
                    </p>
                    <input
                        type="text"
                        value={deleteConfirmationText}
                        onChange={(e) =>
                            setDeleteConfirmationText(e.target.value)
                        }
                        placeholder='e.g. "delete my account"'
                        disabled={isDeleteLoading}
                        className="w-full border border-red-200 rounded-full px-4 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={handleDeleteAccount}
                            disabled={
                                isDeleteLoading ||
                                !isDeleteConfirmed(deleteConfirmationText)
                            }
                            className={`px-6 py-2 rounded-full font-semibold text-sm text-white ${
                                isDeleteLoading ||
                                !isDeleteConfirmed(deleteConfirmationText)
                                    ? "bg-red-300 cursor-not-allowed"
                                    : "bg-red-700 hover:bg-red-800"
                            }`}
                        >
                            {isDeleteLoading ? "Deleting…" : "Permanently delete"}
                        </button>
                        <button
                            type="button"
                            onClick={cancelDeleteAccount}
                            disabled={isDeleteLoading}
                            className="px-6 py-2 rounded-full bg-gray-200 text-gray-900 font-semibold text-sm hover:bg-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountInformation;
